import * as React from 'react';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { Autocomplete, Grid, TextField } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import { DataCollectionData } from 'benchmarking-domain/data-collection';
import { DataPointValue } from 'benchmarking-domain/data-point';
import {
  CalculatedCellAttributes,
  CalculationFormula,
  InputCellAttributes,
  LabelCellAttributes,
  QuestionnaireSectionRowCell,
} from 'benchmarking-domain/questionnaire';
import { NumberHelper } from 'helpers/number';
import { isNumber } from 'mathjs';
import { NumericFormat } from 'react-number-format';

import { DataCollectionHelper } from '../../../helpers/data-collection';
import { useDebugMode } from '../../../hooks/use-debug-mode';

export interface QuestionnaireSectionTableCellProps {
  valueIdPrefix?: string;
  cell: QuestionnaireSectionRowCell;
  dataCollection?: DataCollectionData;
  dataCollectionChangeHandler?: (valueId: string, value: DataPointValue) => Promise<void>;
  globalVariables: DataCollectionData;
}

export function QuestionnaireSectionTableCell({
  valueIdPrefix,
  cell,
  dataCollection,
  dataCollectionChangeHandler,
  globalVariables,
}: QuestionnaireSectionTableCellProps): React.JSX.Element {
  const isDebugMode = useDebugMode();

  const getValueId = useCallback(
    (valueId: string) => {
      return DataCollectionHelper.getValueId(valueId, valueIdPrefix);
    },
    [valueIdPrefix]
  );

  const tryToGetValue = useCallback(
    (value: string) => {
      return DataCollectionHelper.tryToGetValue(value, globalVariables, dataCollection, valueIdPrefix);
    },
    [dataCollection, globalVariables, valueIdPrefix]
  );

  const calculateExpression = useCallback(
    (definition: CalculationFormula) => {
      return DataCollectionHelper.calculateExpression(definition, globalVariables, dataCollection, valueIdPrefix);
    },
    [globalVariables, dataCollection, valueIdPrefix]
  );

  const value = useMemo(() => {
    if (cell.type === 'calculated') {
      const attr = cell.attr as CalculatedCellAttributes;

      return calculateExpression(attr);
    } else if (cell.id) {
      return tryToGetValue(`\${${cell.id}\}`);
    }
  }, [cell.id, cell.attr, cell.type, calculateExpression, tryToGetValue]);

  useEffect(() => {
    if (value != undefined && cell.type === 'calculated' && cell.id) {
      const prevValue = dataCollection?.[getValueId(cell.id)];
      if (value !== prevValue) {
        dataCollectionChangeHandler?.(getValueId(cell.id), value);
      }
    }
  }, [cell.id, cell.type, dataCollection, dataCollectionChangeHandler, value, getValueId]);

  const onValueChanged = (value: DataPointValue) => {
    if (cell.id) {
      dataCollectionChangeHandler?.(getValueId(cell.id), value);
    }
  };

  const convertToDisplay = (cell: QuestionnaireSectionRowCell) => {
    const className = cell.style;
    const colSpan = cell.colSpan;
    const variant = cell.style === 'header' ? 'head' : undefined;
    let align: 'inherit' | 'left' | 'center' | 'right' | 'justify' = 'right';
    let child: ReactElement = <></>;

    if (cell.type === 'label') {
      const attr = cell.attr as LabelCellAttributes | undefined;
      align = attr?.displayValue ? 'left' : 'center';

      child = (
        <Grid container>
          {attr?.displayValue && (
            <Grid item xs={attr?.description ? 8 : 12}>
              {attr?.displayValue}
            </Grid>
          )}
          {attr?.description && (
            <Grid item xs={attr.displayValue ? 4 : 12} alignContent="center">
              <Tooltip title={attr.description}>
                <InfoIcon />
              </Tooltip>
            </Grid>
          )}
        </Grid>
      );
    }

    if (cell.type === 'input') {
      const attr = cell.attr as InputCellAttributes | undefined;
      const predefinedValuesLength = attr?.predefinedValues?.length ?? 0;

      child = (
        <>
          {!attr?.isAutocomplete && predefinedValuesLength > 0 && (
            <Select value={value ?? ''} onChange={(e) => onValueChanged(e.target.value)}>
              <MenuItem value={undefined}>-</MenuItem>
              {attr?.predefinedValues!.map((option, index) => {
                if (option.condition && calculateExpression(option.condition) == false) {
                  return null;
                }
                return (
                  <MenuItem key={index} value={option.value}>
                    {option.displayValue ?? option.value}
                  </MenuItem>
                );
              })}
            </Select>
          )}

          {attr?.isAutocomplete && predefinedValuesLength > 0 && (
            <Autocomplete
              options={attr
                ?.predefinedValues!.filter((o) => !o.condition || calculateExpression(o.condition))
                .map((option, index) => {
                  return option.value;
                })}
              disablePortal
              disableClearable
              freeSolo
              value={value?.toString() ?? ''}
              autoSelect
              onChange={(e, inputValue) => {
                const isUndefined = !inputValue || inputValue === '';

                if (!isUndefined && attr.type === 'number') {
                  const newValue = +inputValue!;
                  if (
                    (attr.minValue != undefined && typeof attr.minValue === 'number' && newValue < attr.minValue) ||
                    (attr.maxValue != undefined && typeof attr.maxValue === 'number' && newValue > attr.maxValue)
                  ) {
                    e.preventDefault();
                    return;
                  }
                }
                onValueChanged(isUndefined ? undefined : attr.type === 'number' ? +inputValue! : inputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    min: attr.minValue ?? 0,
                    max: attr.maxValue,
                    style: { textAlign: 'right' },
                  }}
                />
              )}
            />
          )}

          {predefinedValuesLength === 0 && attr?.type === 'number' && (
            <>
              {isDebugMode && (
                <>
                  {cell.id}
                  <br />
                </>
              )}
              <NumericFormat
                value={(value as number) ?? ''}
                onValueChange={(values) => onValueChanged(values.floatValue)}
                customInput={TextField}
                allowedDecimalSeparators={['.', ',']}
                thousandSeparator={' '}
                inputMode="decimal"
                allowNegative={true}
                isAllowed={(values) => {
                  const { floatValue } = values;
                  const minValue = attr.minValue != undefined ? parseFloat(`${attr.minValue ?? 0}`) : undefined;
                  const maxValue = attr.maxValue != undefined ? parseFloat(`${attr.maxValue}`) : undefined;
                  return (
                    floatValue === undefined ||
                    ((minValue == undefined || floatValue >= minValue) &&
                      (maxValue == undefined || floatValue <= maxValue))
                  );
                }}
                InputProps={{
                  // onPaste can be removed once this PR is merged: https://github.com/s-yadav/react-number-format/pull/556
                  onPaste: (e: React.ClipboardEvent) => {
                    let pastedText = e.clipboardData.getData('text');
                    if (pastedText.includes(',')) {
                      e.preventDefault();
                      onValueChanged(parseFloat(pastedText.replace(/\s+/g, '').replace(',', '.')));
                    }
                  },
                  inputProps: {
                    style: { textAlign: 'right' },
                  },
                }}
              />
            </>
          )}

          {predefinedValuesLength === 0 && attr?.type !== 'number' && (
            <>
              {isDebugMode && (
                <>
                  {' '}
                  {cell.id} <br />{' '}
                </>
              )}
              <TextField
                type={attr?.type}
                value={value ?? ''}
                onChange={(e) => {
                  const isUndefined = e.target.value === '';
                  onValueChanged(isUndefined ? undefined : e.target.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </>
          )}
        </>
      );
    }

    if (cell.type === 'calculated') {
      const formatedValue = isNumber(value) ? NumberHelper.formatNumber(value) : value;
      const calculatedCellDisplayValue = isDebugMode ? cell.id?.toString() + ': ' + formatedValue : formatedValue;
      child = <>{calculatedCellDisplayValue}</>;
    }

    return (
      <TableCell variant={variant} className={className} colSpan={colSpan} align={align}>
        {child}
      </TableCell>
    );
  };

  return <>{convertToDisplay(cell)}</>;
}
